import { motion } from 'framer-motion'
import { CheckCircle, Award, Users, Globe } from 'lucide-react'
import { intro, philosophy, clientJourney } from '@/content/site'
import { CalendlyBtn } from '@/components/ui/calendly-btn'

const credentials = [
  { icon: Award, text: 'Certified IFS-Informed Coach' },
  { icon: Users, text: '20+ Years Supporting Life Transitions' },
  { icon: Globe, text: 'Working Virtually with Clients Worldwide' },
  { icon: CheckCircle, text: 'Specializing in Midlife Professionals' },
]

export default function AboutPage() {
  return (
    <div className="bg-white dark:bg-slate-950 min-h-screen">

      {/* Page header */}
      <div className="bg-slate-900 pt-28 pb-16 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }}
        />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="text-blue-400 text-sm font-semibold tracking-widest uppercase mb-3">
            About Douglas
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.05 }}
            className="text-4xl sm:text-5xl font-bold text-white mb-4">
            {intro.heading}
          </motion.h1>
        </div>
      </div>

      {/* Bio + photo */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-16 items-start mb-20">
          <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
            <div className="space-y-5 mb-8">
              {intro.body.map((para, i) => (
                <p key={i} className="text-lg text-slate-600 dark:text-slate-300 leading-relaxed">{para}</p>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-4 mb-8">
              {credentials.map(({ icon: Icon, text }, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-slate-800 rounded-xl">
                  <Icon size={18} className="text-blue-600 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-slate-700 dark:text-slate-300 font-medium">{text}</span>
                </motion.div>
              ))}
            </div>
            <CalendlyBtn size="lg" className="bg-blue-600 hover:bg-blue-700 text-white border-transparent">
              Work with Douglas
            </CalendlyBtn>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.1 }}
            className="flex justify-center">
            <div className="relative">
              <motion.div
                animate={{ borderRadius: ['60% 40% 30% 70% / 60% 30% 70% 40%', '30% 60% 70% 40% / 50% 60% 30% 60%', '60% 40% 30% 70% / 60% 30% 70% 40%'] }}
                transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
                className="absolute -inset-4 bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30"
              />
              <motion.div
                animate={{ borderRadius: ['60% 40% 30% 70% / 60% 30% 70% 40%', '30% 60% 70% 40% / 50% 60% 30% 60%', '60% 40% 30% 70% / 60% 30% 70% 40%'] }}
                transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
                className="relative w-72 h-72 sm:w-96 sm:h-96 overflow-hidden"
              >
                <img
                  src={`${import.meta.env.BASE_URL}profile-photo-2.jpg`}
                  alt="Douglas P Nelson"
                  className="w-full h-full object-cover object-center"
                />
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Philosophy */}
        <div className="bg-slate-900 rounded-3xl p-10 mb-16">
          <motion.h2 initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="text-2xl sm:text-3xl font-bold text-white mb-6">
            {philosophy.heading}
          </motion.h2>
          <div className="space-y-4">
            {philosophy.body.map((para, i) => (
              <motion.p key={i} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="text-slate-300 leading-relaxed text-lg">
                {para}
              </motion.p>
            ))}
          </div>
        </div>

        {/* Client journey */}
        <div>
          <motion.h2 initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="text-3xl font-bold text-slate-900 dark:text-white mb-3">
            {clientJourney.heading}
          </motion.h2>
          <motion.p initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}
            className="text-slate-500 dark:text-slate-400 mb-12 text-lg">
            {clientJourney.subheading}
          </motion.p>
          <div className="space-y-8">
            {clientJourney.steps.map((step, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="flex gap-6 p-6 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-700">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-sm">
                    {String(i + 1).padStart(2, '0')}
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-1">{step.title}</h3>
                  {step.subtitle && <p className="text-blue-600 dark:text-blue-400 text-sm font-medium mb-3 italic">{step.subtitle}</p>}
                  <p className="text-slate-600 dark:text-slate-300 leading-relaxed mb-4">{step.body}</p>
                  {step.bullets && (
                    <ul className="space-y-1.5">
                      {step.bullets.map((b, j) => (
                        <li key={j} className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-400">
                          <CheckCircle size={15} className="text-blue-500 mt-0.5 flex-shrink-0" />
                          {b}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-slate-900 py-16">
        <div className="max-w-2xl mx-auto text-center px-4">
          <h2 className="text-2xl font-bold text-white mb-3">Begin your journey with Douglas</h2>
          <p className="text-slate-400 mb-8">A free consultation. A real conversation. No pressure.</p>
          <CalendlyBtn size="lg" className="bg-blue-600 hover:bg-blue-700 text-white border-transparent">
            Book Free Consultation
          </CalendlyBtn>
        </div>
      </div>
    </div>
  )
}
